'use client';

import { LocateFixed, MapPin } from 'lucide-react';
import dynamic from 'next/dynamic';
import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ShopifyImage } from '@/components/ui/shopify-image';
import { directionsUrl, distanceMiles, formatStoreAddress, hasCoordinates, type LatLng, type StoreLocation } from './store-locator-data';
import type { MapFocus } from './store-map';
import { StoreMapBoundary } from './store-map-boundary';
const StoreMap = dynamic(() => import('./store-map'), {
  ssr: false,
  loading: () => <div className="flex h-full w-full items-center justify-center bg-neutral-100 text-xs font-semibold uppercase tracking-[0.18em] text-neutral-400">
			Loading map…
		</div>
});
interface StoreLocatorProps {
  stores: StoreLocation[];
  heroImageUrl?: string | null;
}
type SearchStatus = 'idle' | 'searching' | 'locating';
const SEARCH_ZOOM = 9;
const STORE_ZOOM = 13;
async function geocodeQuery(query: string): Promise<LatLng | null> {
  const params = new URLSearchParams({
    format: 'jsonv2',
    limit: '1',
    countrycodes: 'us,ca',
    q: query
  });
  const response = await fetch(`https://nominatim.openstreetmap.org/search?${params.toString()}`);
  if (!response.ok) {
    throw new Error('Geocoding failed');
  }
  const results = (await response.json()) as Array<{
    lat?: string;
    lon?: string;
  }>;
  const first = results?.[0];
  const lat = Number.parseFloat(first?.lat ?? '');
  const lng = Number.parseFloat(first?.lon ?? '');
  return Number.isFinite(lat) && Number.isFinite(lng) ? {
    lat,
    lng
  } : null;
}
function formatDistance(distance: number) {
  return `${distance < 10 ? distance.toFixed(1) : Math.round(distance)} mi`;
}
export function StoreLocator({
  stores,
  heroImageUrl
}: StoreLocatorProps) {
  const [query, setQuery] = useState('');
  const [origin, setOrigin] = useState<LatLng | null>(null);
  const [focus, setFocus] = useState<MapFocus | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [status, setStatus] = useState<SearchStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [moreBelow, setMoreBelow] = useState(false);

  // Bottom fade signals there are more stores to scroll to; it clears at the end.
  const updateListOverflow = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    const canScroll = el.scrollHeight > el.clientHeight + 4;
    const atEnd = el.scrollTop + el.clientHeight >= el.scrollHeight - 12;
    setMoreBelow(canScroll && !atEnd);
  }, []);
  useEffect(() => {
    updateListOverflow();
    window.addEventListener('resize', updateListOverflow);
    return () => window.removeEventListener('resize', updateListOverflow);
  }, [updateListOverflow]);

  // Re-measure when the list content changes (e.g. after a distance sort).
  // biome-ignore lint/correctness/useExhaustiveDependencies: origin is the proxy for list content changing
  useEffect(() => {
    updateListOverflow();
  }, [origin, updateListOverflow]);
  const sortedStores = useMemo(() => {
    if (!origin) {
      return stores.map(store => ({
        store,
        distance: null as number | null
      }));
    }
    return stores.map(store_0 => ({
      store: store_0,
      distance: hasCoordinates(store_0) ? distanceMiles(origin, {
        lat: store_0.latitude,
        lng: store_0.longitude
      }) : null
    })).sort((a, b) => (a.distance ?? Number.POSITIVE_INFINITY) - (b.distance ?? Number.POSITIVE_INFINITY));
  }, [stores, origin]);

  // Selecting a pin on the map brings its row into view.
  useEffect(() => {
    if (selectedId != null) {
      document.getElementById(`store-row-${selectedId}`)?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth'
      });
    }
  }, [selectedId]);
  function applyOrigin(point: LatLng) {
    setOrigin(point);
    setSelectedId(null);
    setFocus({
      center: point,
      zoom: SEARCH_ZOOM
    });
  }
  async function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmed = query.trim();
    if (!trimmed || status !== 'idle') return;
    setStatus('searching');
    setError(null);
    try {
      const point_0 = await geocodeQuery(trimmed);
      if (point_0) {
        applyOrigin(point_0);
      } else {
        setError('We couldn’t find that location. Try a city, state, or ZIP code.');
      }
    } catch {
      setError('Location search is unavailable right now. Please try again in a moment.');
    } finally {
      setStatus('idle');
    }
  }
  function handleUseMyLocation() {
    if (status !== 'idle') return;
    if (!navigator.geolocation) {
      setError('Your browser doesn’t support location access.');
      return;
    }
    setStatus('locating');
    setError(null);
    navigator.geolocation.getCurrentPosition(position => {
      applyOrigin({
        lat: position.coords.latitude,
        lng: position.coords.longitude
      });
      setStatus('idle');
    }, () => {
      setError('We couldn’t access your location. Try searching by city or ZIP instead.');
      setStatus('idle');
    }, {
      timeout: 10000
    });
  }
  function handleSelectStore(id: number) {
    setSelectedId(id);
    const store_1 = stores.find(entry => entry.id === id);
    if (store_1 && hasCoordinates(store_1)) {
      setFocus({
        center: {
          lat: store_1.latitude,
          lng: store_1.longitude
        },
        zoom: STORE_ZOOM
      });
    }
  }
  return <>
			<section className="relative overflow-hidden bg-neutral-950 text-white">
				{heroImageUrl && <div className="absolute inset-0">
						<ShopifyImage src={heroImageUrl} alt="" fill loading="eager" fetchPriority="high" decoding="sync" sizes="100vw" className="object-cover grayscale" />
						<div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/55 to-black/40" />
					</div>}
				<div className="container-page relative z-10 pb-14 pt-16 text-center md:pb-20 md:pt-24">
					<h1 className="text-4xl font-black uppercase leading-[0.95] tracking-tight sm:text-5xl md:text-6xl">
						Find a Store
					</h1>
					<p className="mx-auto mt-4 max-w-xl text-sm text-white/60 md:text-base">
						Workman gear, stocked near you. Search by city, state, or ZIP code.
					</p>

					{stores.length > 0 && <>
							<search>
								<form onSubmit={handleSearch} className="mx-auto mt-8 flex w-full max-w-2xl overflow-hidden rounded-md border border-white/20 bg-neutral-900/70 backdrop-blur-sm">
									<label htmlFor="store-search" className="sr-only">
										Search by city, state, or ZIP code
									</label>
									<input id="store-search" type="search" value={query} onChange={event_0 => setQuery(event_0.target.value)} placeholder="City, state, or ZIP code" autoComplete="postal-code" className="min-w-0 flex-1 bg-transparent px-4 py-3.5 text-sm text-white placeholder:text-white/50 focus:outline-none md:px-5" />
									<button type="submit" disabled={status !== 'idle' || query.trim().length === 0} className="bg-neutral-950 px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.1em] text-white transition-colors hover:bg-neutral-800 disabled:cursor-not-allowed disabled:text-white/40 md:px-8">
										{status === 'searching' ? 'Searching…' : 'Search'}
									</button>
								</form>
							</search>

							<button type="button" onClick={handleUseMyLocation} disabled={status !== 'idle'} className="mt-5 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-white/60 transition-colors hover:text-white disabled:cursor-not-allowed">
								<LocateFixed className="h-3.5 w-3.5" aria-hidden="true" />
								{status === 'locating' ? 'Locating…' : 'Use my location'}
							</button>

							<div aria-live="polite" className="mt-3 min-h-5">
								{error && <p className="text-sm text-red-300">{error}</p>}
							</div>
						</>}
				</div>
			</section>

			<section className="bg-white py-12 md:py-16">
				<div className="container-page">
					{stores.length === 0 ? <div className="mx-auto max-w-lg py-10 text-center md:py-16">
							<MapPin className="mx-auto h-8 w-8 text-neutral-300" aria-hidden="true" />
							<h2 className="mt-5 text-xl font-black uppercase tracking-[0.12em] text-neutral-950">
								Locations coming soon
							</h2>
							<p className="mt-3 text-neutral-600">
								We’re adding stockists all the time. Check back soon to find our gear near you.
							</p>
						</div> : <>
							<div className="mb-6 flex items-baseline justify-between gap-4 border-b border-neutral-200 pb-4">
								<h2 className="text-[13px] font-black uppercase tracking-[0.18em] text-neutral-950" aria-live="polite">
									{origin ? 'Nearest to you' : 'All locations'}
								</h2>
								<span className="text-xs font-semibold uppercase tracking-[0.14em] text-neutral-500">
									{stores.length} {stores.length === 1 ? 'store' : 'stores'}
								</span>
							</div>

							<div className="grid gap-8 lg:grid-cols-[24rem_1fr] lg:gap-10">
								<div className="relative order-2 lg:order-1">
									<ul ref={listRef} onScroll={updateListOverflow} className="store-list max-h-[480px] divide-y divide-neutral-200 overflow-y-auto lg:max-h-[640px]">
										{sortedStores.map(({
                  store: store_2,
                  distance
                }) => {
                  const selected = store_2.id === selectedId;
                  return <li key={store_2.id} id={`store-row-${store_2.id}`}>
													<div className={`relative border-l-2 py-5 pl-4 pr-3 transition-colors ${selected ? 'border-brand-accent bg-cream' : 'border-transparent hover:bg-neutral-50'}`}>
														<div className="flex items-baseline justify-between gap-4">
															<h3 className="text-[13px] font-black uppercase tracking-[0.18em] text-neutral-950 sm:text-[15px] sm:tracking-[0.14em]">
																<button type="button" onClick={() => handleSelectStore(store_2.id)} aria-pressed={selected} className="text-left uppercase after:absolute after:inset-0 after:content-['']">
																	{store_2.name}
																</button>
															</h3>
															{distance != null && <span className="shrink-0 text-xs font-black uppercase tracking-[0.14em] text-neutral-950">
																	{formatDistance(distance)}
																</span>}
														</div>
														<p className="mt-1.5 text-sm text-neutral-600">
															{formatStoreAddress(store_2)}
														</p>
														{store_2.hours && <p className="mt-1.5 whitespace-pre-line text-xs leading-relaxed text-neutral-500">
																{store_2.hours}
															</p>}
														<div className="relative z-10 mt-3.5 flex flex-wrap items-center gap-x-5 gap-y-2">
															<a href={directionsUrl(store_2)} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-[0.18em] text-brand-accent">
																Get directions
																<span aria-hidden="true" className="transition-transform group-hover:translate-x-1">
																	&rarr;
																</span>
															</a>
															{store_2.website && <a href={store_2.website} target="_blank" rel="noopener noreferrer" className="text-[11px] font-black uppercase tracking-[0.18em] text-neutral-950 transition-colors hover:text-neutral-600">
																	Website
																</a>}
															{store_2.phone && <a href={`tel:${store_2.phone.replace(/[^+\d]/g, '')}`} className="text-sm text-neutral-600 transition-colors hover:text-neutral-950">
																	{store_2.phone}
																</a>}
														</div>
													</div>
												</li>;
                })}
									</ul>
									<div aria-hidden="true" className={`pointer-events-none absolute inset-x-0 bottom-0 flex h-24 items-end justify-center bg-gradient-to-t from-white via-white/80 to-transparent pb-1 transition-opacity duration-300 ${moreBelow ? 'opacity-100' : 'opacity-0'}`}>
										<span className="text-[10px] font-black uppercase tracking-[0.22em] text-neutral-400">
											Scroll for more &darr;
										</span>
									</div>
								</div>

								<div className="relative z-0 order-1 h-[320px] isolate border border-neutral-200 sm:h-[420px] lg:order-2 lg:h-[640px]">
									<StoreMapBoundary>
										<StoreMap stores={stores} focus={focus} selectedId={selectedId} onSelect={handleSelectStore} />
									</StoreMapBoundary>
								</div>
							</div>
						</>}
				</div>
			</section>
		</>;
}